import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, Button, IconButton } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import GetAppIcon from '@material-ui/icons/GetApp';
import SendIcon from '@material-ui/icons/Send';
import SignalCellularAltIcon from '@material-ui/icons/SignalCellularAlt';
import WifiIcon from '@material-ui/icons/Wifi';
import BatteryFullIcon from '@material-ui/icons/BatteryFull';
import { GoogleMap, Polyline, Marker, useJsApiLoader } from '@react-google-maps/api';
import { exportDriverPdf } from '../../utils/exportPdf';

const GOOGLE_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;
const LIBRARIES = ['places'];

/**
 * Centered modal showing an Android phone mockup with the route map and key
 * details inside. Used for demo presentations — the operator sees exactly what
 * the driver will see when the route is sent to their phone.
 *
 * Props:
 *   open          — bool
 *   route         — current route object (with computedRoute.geometry.coordinates)
 *   vehiclePlate  — string
 *   driverName    — string
 *   lang          — 'pt' | 'en'
 *   onClose       — fn()
 *   onSend        — optional fn()
 */
function DriverPdfPreviewDialog({ open, route, vehiclePlate, driverName, lang = 'pt', onClose, onSend, otNotesById }) {
  const [now, setNow] = useState(() => new Date());
  const [mapRef, setMapRef] = useState(null);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_KEY,
    libraries: LIBRARIES,
    language: 'pt',
    region: 'PT',
  });

  // Live clock for the phone status bar
  useEffect(() => {
    if (!open) return;
    const t = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(t);
  }, [open]);

  // Extract route coords for the mini map
  const routePath = useMemo(() => {
    const coords = route?.computedRoute?.geometry?.coordinates || [];
    return coords.map(c => ({ lat: c[1], lng: c[0] }));
  }, [route]);

  // Fit map to route bounds whenever the map is ready or route changes
  useEffect(() => {
    if (!mapRef || !isLoaded || routePath.length === 0 || !window.google?.maps) return;
    const bounds = new window.google.maps.LatLngBounds();
    routePath.forEach(p => bounds.extend(p));
    mapRef.fitBounds(bounds, { top: 20, right: 20, bottom: 20, left: 20 });
  }, [mapRef, isLoaded, routePath]);

  if (!open) return null;

  const timeStr = now.toLocaleTimeString(lang === 'pt' ? 'pt-PT' : 'en-GB', { hour: '2-digit', minute: '2-digit' });
  const dateStr = now.toLocaleDateString(lang === 'pt' ? 'pt-PT' : 'en-GB', { weekday: 'short', day: '2-digit', month: 'short' });

  const cr = route?.computedRoute;
  const distKm = cr?.distance ? (cr.distance / 1000).toFixed(1) : '—';
  const durSec = cr?.duration || 0;
  const durH = Math.floor(durSec / 3600);
  const durM = Math.round((durSec % 3600) / 60);
  const durLabel = durSec ? `${durH}h${String(durM).padStart(2, '0')}` : '—';

  const originName = (typeof route?.origin === 'string' ? route.origin : route?.origin?.name)?.split(',')[0] || '?';
  const destName = (typeof route?.destination === 'string' ? route.destination : route?.destination?.name)?.split(',')[0] || '?';
  const originLngLat = Array.isArray(route?.origin?.coordinates) ? route.origin.coordinates : null;
  const destLngLat = Array.isArray(route?.destination?.coordinates) ? route.destination.coordinates : null;

  function handleDownload() {
    try {
      exportDriverPdf(route, vehiclePlate, driverName, { otNotesById: otNotesById || {} });
    } catch (e) {
      console.error('PDF generation error:', e);
    }
  }

  return (
    <Box style={{
      position: 'fixed', inset: 0,
      background: 'radial-gradient(ellipse at center, rgba(30,41,59,0.88) 0%, rgba(15,23,42,0.95) 100%)',
      backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
      zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }} onClick={onClose}>

      {/* Centered modal card */}
      <Box onClick={(e) => e.stopPropagation()} style={{
        background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
        borderRadius: 20,
        boxShadow: '0 40px 100px rgba(0,0,0,0.5), 0 0 140px rgba(59,130,246,0.18)',
        padding: 28,
        maxWidth: '96vw', maxHeight: '96vh',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', gap: 18,
        position: 'relative',
      }}>
        {/* Title */}
        <Box style={{ textAlign: 'center' }}>
          <Typography style={{ fontSize: '1.15rem', fontWeight: 800, color: '#0f172a' }}>
            {lang === 'pt' ? 'Pré-visualização para o motorista' : 'Driver preview'}
          </Typography>
          <Typography style={{ fontSize: '0.74rem', color: '#64748b', marginTop: 3 }}>
            {lang === 'pt' ? 'Isto é o que o motorista vai receber no telemóvel' : 'This is what the driver will see on their phone'}
          </Typography>
        </Box>

        {/* Close button */}
        <IconButton onClick={onClose}
          style={{
            position: 'absolute', top: 14, right: 14,
            background: '#f1f5f9', color: '#64748b',
            width: 32, height: 32,
          }}>
          <CloseIcon style={{ fontSize: 16 }} />
        </IconButton>

        {/* Phone mockup */}
        <Box style={{
          position: 'relative',
          width: 340, height: 680, maxHeight: 'calc(92vh - 160px)',
          background: '#0f172a',
          borderRadius: 42,
          padding: 11,
          boxShadow: `
            0 0 0 2px #1e293b,
            0 0 0 3px #334155,
            inset 0 1px 0 rgba(255,255,255,0.06),
            0 20px 50px rgba(0,0,0,0.4)
          `,
        }}>
          {/* Side buttons */}
          <Box style={{ position: 'absolute', left: -3, top: 100, width: 3, height: 34, borderRadius: '2px 0 0 2px', background: '#334155' }} />
          <Box style={{ position: 'absolute', left: -3, top: 148, width: 3, height: 56, borderRadius: '2px 0 0 2px', background: '#334155' }} />
          <Box style={{ position: 'absolute', left: -3, top: 214, width: 3, height: 56, borderRadius: '2px 0 0 2px', background: '#334155' }} />
          <Box style={{ position: 'absolute', right: -3, top: 168, width: 3, height: 74, borderRadius: '0 2px 2px 0', background: '#334155' }} />

          {/* Screen */}
          <Box style={{
            width: '100%', height: '100%',
            background: '#f1f5f9',
            borderRadius: 31,
            overflow: 'hidden',
            display: 'flex', flexDirection: 'column',
            position: 'relative',
          }}>
            {/* Punch-hole */}
            <Box style={{
              position: 'absolute', top: 10, left: '50%', transform: 'translateX(-50%)',
              width: 10, height: 10, borderRadius: '50%',
              background: '#0f172a', zIndex: 10,
            }} />

            {/* Status bar */}
            <Box style={{
              height: 26, flexShrink: 0,
              background: '#0f172a', color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '0 18px',
              fontSize: '0.66rem', fontWeight: 600,
            }}>
              <span>{timeStr}</span>
              <Box style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                <SignalCellularAltIcon style={{ fontSize: 11 }} />
                <WifiIcon style={{ fontSize: 11 }} />
                <BatteryFullIcon style={{ fontSize: 13, transform: 'rotate(90deg)' }} />
              </Box>
            </Box>

            {/* App header */}
            <Box style={{
              height: 52, flexShrink: 0,
              background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
              color: '#fff',
              display: 'flex', alignItems: 'center',
              padding: '0 12px', gap: 10,
            }}>
              <Box style={{
                width: 30, height: 30, borderRadius: 8,
                background: 'rgba(255,255,255,0.22)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 16, flexShrink: 0,
              }}>🚛</Box>
              <Box style={{ flex: 1, minWidth: 0 }}>
                <Typography style={{ fontSize: '0.78rem', fontWeight: 800, lineHeight: 1.1, color: '#fff' }}>
                  EcooGPS Driver
                </Typography>
                <Typography style={{ fontSize: '0.56rem', color: 'rgba(255,255,255,0.75)', fontWeight: 500 }}>
                  {dateStr} · {driverName || (lang === 'pt' ? 'Motorista' : 'Driver')}
                </Typography>
              </Box>
              <Box style={{
                padding: '3px 8px', borderRadius: 12,
                background: 'rgba(34,197,94,0.25)',
                border: '1px solid rgba(34,197,94,0.5)',
                fontSize: '0.52rem', fontWeight: 800,
                display: 'flex', alignItems: 'center', gap: 3,
              }}>
                <Box style={{ width: 5, height: 5, borderRadius: '50%', background: '#22c55e' }} />
                ONLINE
              </Box>
            </Box>

            {/* "Nova rota" notification */}
            <Box style={{
              margin: '10px 11px 0', padding: '8px 11px',
              background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
              border: '1px solid #facc15',
              borderRadius: 10,
              display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0,
            }}>
              <Box style={{
                width: 26, height: 26, borderRadius: '50%',
                background: '#f59e0b',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', fontSize: 13, flexShrink: 0,
                animation: 'pulse-bell 1.5s ease-in-out infinite',
              }}>🔔</Box>
              <Box style={{ flex: 1, minWidth: 0 }}>
                <Typography style={{ fontSize: '0.66rem', fontWeight: 800, color: '#92400e', lineHeight: 1.2 }}>
                  {lang === 'pt' ? 'Nova rota recebida' : 'New route received'}
                </Typography>
                <Typography style={{ fontSize: '0.54rem', color: '#b45309', lineHeight: 1.2 }}>
                  {vehiclePlate && <>{vehiclePlate} · </>}
                  {route?.name || (lang === 'pt' ? 'Sem nome' : 'Unnamed')}
                </Typography>
              </Box>
              <Box style={{ fontSize: '0.54rem', color: '#b45309', fontWeight: 600, whiteSpace: 'nowrap' }}>
                {timeStr}
              </Box>
            </Box>

            {/* Mini map with route */}
            <Box style={{ margin: '10px 11px 0', flexShrink: 0 }}>
              <Box style={{
                height: 240, borderRadius: 10, overflow: 'hidden',
                border: '1px solid #cbd5e1',
                boxShadow: '0 2px 8px rgba(15,23,42,0.08)',
                background: '#e2e8f0',
                position: 'relative',
              }}>
                {isLoaded && routePath.length > 0 ? (
                  <GoogleMap
                    mapContainerStyle={{ width: '100%', height: '100%' }}
                    center={routePath[0]}
                    zoom={10}
                    onLoad={setMapRef}
                    options={{
                      disableDefaultUI: true,
                      zoomControl: false,
                      gestureHandling: 'none',
                      styles: [
                        { featureType: 'poi', stylers: [{ visibility: 'off' }] },
                        { featureType: 'transit', stylers: [{ visibility: 'off' }] },
                      ],
                    }}
                  >
                    {/* Route outline (white) */}
                    <Polyline
                      path={routePath}
                      options={{
                        strokeColor: '#ffffff',
                        strokeOpacity: 1,
                        strokeWeight: 7,
                        zIndex: 1,
                      }}
                    />
                    {/* Route main (blue gradient) */}
                    <Polyline
                      path={routePath}
                      options={{
                        strokeColor: '#3b82f6',
                        strokeOpacity: 1,
                        strokeWeight: 4,
                        zIndex: 2,
                      }}
                    />
                    {/* Origin marker (green) */}
                    {originLngLat && (
                      <Marker
                        position={{ lat: originLngLat[1], lng: originLngLat[0] }}
                        icon={{
                          path: window.google.maps.SymbolPath.CIRCLE,
                          scale: 7,
                          fillColor: '#22c55e',
                          fillOpacity: 1,
                          strokeColor: '#fff',
                          strokeWeight: 2,
                        }}
                      />
                    )}
                    {/* Destination marker (red) */}
                    {destLngLat && (
                      <Marker
                        position={{ lat: destLngLat[1], lng: destLngLat[0] }}
                        icon={{
                          path: window.google.maps.SymbolPath.CIRCLE,
                          scale: 7,
                          fillColor: '#ef4444',
                          fillOpacity: 1,
                          strokeColor: '#fff',
                          strokeWeight: 2,
                        }}
                      />
                    )}
                  </GoogleMap>
                ) : (
                  <Box style={{
                    height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#94a3b8', fontSize: '0.7rem', fontWeight: 600,
                  }}>
                    {!isLoaded
                      ? (lang === 'pt' ? 'A carregar mapa…' : 'Loading map…')
                      : (lang === 'pt' ? 'Sem rota calculada' : 'No route calculated')}
                  </Box>
                )}
              </Box>
            </Box>

            {/* Route summary card */}
            <Box style={{ margin: '10px 11px 0', flex: 1, minHeight: 0, overflow: 'hidden' }}>
              <Box style={{
                background: '#fff', borderRadius: 10, padding: '10px 12px',
                border: '1px solid #e2e8f0',
                boxShadow: '0 1px 3px rgba(15,23,42,0.05)',
              }}>
                {/* Origin → Destination */}
                <Box style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <Box style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
                    flexShrink: 0,
                  }}>
                    <Box style={{ width: 8, height: 8, borderRadius: '50%', background: '#22c55e', border: '2px solid #dcfce7' }} />
                    <Box style={{ width: 2, height: 14, background: '#cbd5e1' }} />
                    <Box style={{ width: 8, height: 8, borderRadius: '50%', background: '#ef4444', border: '2px solid #fee2e2' }} />
                  </Box>
                  <Box style={{ flex: 1, minWidth: 0 }}>
                    <Typography style={{ fontSize: '0.66rem', fontWeight: 700, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {originName}
                    </Typography>
                    <Typography style={{ fontSize: '0.58rem', color: '#94a3b8', fontWeight: 600, margin: '2px 0' }}>
                      {lang === 'pt' ? 'para' : 'to'}
                    </Typography>
                    <Typography style={{ fontSize: '0.66rem', fontWeight: 700, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {destName}
                    </Typography>
                  </Box>
                </Box>
                {/* Stats row */}
                <Box style={{
                  display: 'flex', gap: 8,
                  paddingTop: 8, borderTop: '1px solid #f1f5f9',
                }}>
                  <Box style={{ flex: 1, textAlign: 'center' }}>
                    <Typography style={{ fontSize: '0.5rem', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      {lang === 'pt' ? 'Distância' : 'Distance'}
                    </Typography>
                    <Typography style={{ fontSize: '0.82rem', fontWeight: 800, color: '#1e40af', lineHeight: 1.1 }}>
                      {distKm} km
                    </Typography>
                  </Box>
                  <Box style={{ width: 1, background: '#e2e8f0' }} />
                  <Box style={{ flex: 1, textAlign: 'center' }}>
                    <Typography style={{ fontSize: '0.5rem', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      {lang === 'pt' ? 'Duração' : 'Duration'}
                    </Typography>
                    <Typography style={{ fontSize: '0.82rem', fontWeight: 800, color: '#059669', lineHeight: 1.1 }}>
                      {durLabel}
                    </Typography>
                  </Box>
                  <Box style={{ width: 1, background: '#e2e8f0' }} />
                  <Box style={{ flex: 1, textAlign: 'center' }}>
                    <Typography style={{ fontSize: '0.5rem', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      {lang === 'pt' ? 'Paragens' : 'Stops'}
                    </Typography>
                    <Typography style={{ fontSize: '0.82rem', fontWeight: 800, color: '#f59e0b', lineHeight: 1.1 }}>
                      {route?.stops?.length || 0}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* Android home indicator */}
            <Box style={{
              height: 16, flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              marginTop: 6,
            }}>
              <Box style={{ width: 90, height: 4, borderRadius: 2, background: '#94a3b8' }} />
            </Box>
          </Box>
        </Box>

        {/* Action bar */}
        <Box style={{
          display: 'flex', gap: 10, alignItems: 'center',
        }}>
          <Button
            size="small"
            onClick={onClose}
            style={{
              fontSize: '0.76rem', fontWeight: 700, textTransform: 'none',
              color: '#64748b', padding: '7px 18px', borderRadius: 10,
            }}
          >
            {lang === 'pt' ? 'Fechar' : 'Close'}
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleDownload}
            startIcon={<GetAppIcon style={{ fontSize: 15 }} />}
            style={{
              fontSize: '0.76rem', fontWeight: 700, textTransform: 'none',
              background: 'linear-gradient(135deg, #3b82f6, #2563eb)', color: '#fff',
              borderRadius: 10, padding: '7px 20px',
              boxShadow: '0 4px 12px rgba(59,130,246,0.35)',
            }}
          >
            {lang === 'pt' ? 'Transferir PDF' : 'Download PDF'}
          </Button>
          {onSend && (
            <Button
              size="small"
              variant="contained"
              onClick={onSend}
              startIcon={<SendIcon style={{ fontSize: 15 }} />}
              style={{
                fontSize: '0.76rem', fontWeight: 700, textTransform: 'none',
                background: 'linear-gradient(135deg, #22c55e, #16a34a)', color: '#fff',
                borderRadius: 10, padding: '7px 20px',
                boxShadow: '0 4px 12px rgba(34,197,94,0.35)',
              }}
            >
              {lang === 'pt' ? 'Enviar ao motorista' : 'Send to driver'}
            </Button>
          )}
        </Box>
      </Box>

      <style>{`
        @keyframes pulse-bell {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.12); }
        }
      `}</style>
    </Box>
  );
}

export default DriverPdfPreviewDialog;
